import {useEffect, useRef, type ReactNode} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {clsx} from 'clsx'

import {BackgroundGrid, type BackgroundGridProps} from '../BackgroundGrid'
import styles from './Product.module.css'
import {getPointerPositionPercent, lerp} from './product-helpers'

const RING_POSITION_LERP_FACTOR = 0.15
const RING_POSITION_SETTLE_THRESHOLD = 0.1

type RingPosition = {
  x: number
  y: number
}

export type ProductMediaPosition = 'center' | 'center-left' | 'center-right' | 'bottom-center'

export type ProductBackgroundProps = Omit<BackgroundGridProps, 'className'>

export type ProductProps = {
  backgroundProps?: ProductBackgroundProps
  children: ReactNode
  className?: string
  firstElement?: ReactNode
  firstElementClass?: string
  hasRingShine?: boolean
  hasRoundedCorners?: boolean
  mediaPosition?: ProductMediaPosition
  minPadding?: string
  ringColor?: string
  theme?: BackgroundGridProps['theme']
}

function setRingShinePosition(element: HTMLDivElement, position: RingPosition) {
  element.style.setProperty('--product-ring-x', `${position.x}%`)
  element.style.setProperty('--product-ring-y', `${position.y}%`)
}

export function Product({
  backgroundProps,
  children,
  className,
  firstElement,
  firstElementClass,
  hasRingShine = false,
  hasRoundedCorners = false,
  mediaPosition = 'center',
  minPadding = 'var(--base-size-24)',
  ringColor = '#08872b',
  theme = backgroundProps?.theme ?? 'light',
}: ProductProps) {
  const productRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = usePrefersReducedMotion()
  const canShowRingShine = hasRingShine && !prefersReducedMotion
  const hasFirstElement = firstElement !== undefined && firstElement !== null

  useEffect(() => {
    const product = productRef.current

    if (!canShowRingShine || !product) {
      return
    }

    let animationFrame: number | null = null
    let currentPosition: RingPosition | null = null
    let targetPosition: RingPosition | null = null

    const animateRingShine = () => {
      if (!currentPosition || !targetPosition) {
        animationFrame = null
        return
      }

      const hasSettled =
        Math.abs(targetPosition.x - currentPosition.x) <= RING_POSITION_SETTLE_THRESHOLD &&
        Math.abs(targetPosition.y - currentPosition.y) <= RING_POSITION_SETTLE_THRESHOLD
      const nextPosition = hasSettled
        ? targetPosition
        : {
            x: lerp(currentPosition.x, targetPosition.x, RING_POSITION_LERP_FACTOR),
            y: lerp(currentPosition.y, targetPosition.y, RING_POSITION_LERP_FACTOR),
          }

      currentPosition = nextPosition
      setRingShinePosition(product, nextPosition)
      animationFrame = hasSettled ? null : window.requestAnimationFrame(animateRingShine)
    }

    const handlePointerMove = (event: PointerEvent) => {
      const position = getPointerPositionPercent(event.clientX, event.clientY, product.getBoundingClientRect())
      targetPosition = position

      if (currentPosition === null) {
        currentPosition = position
        setRingShinePosition(product, position)
      } else if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(animateRingShine)
      }
    }

    const resetRingShine = () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame)
      }
      animationFrame = null
      currentPosition = null
      targetPosition = null
    }

    product.addEventListener('pointerleave', resetRingShine)
    product.addEventListener('pointermove', handlePointerMove)

    return () => {
      resetRingShine()
      product.removeEventListener('pointerleave', resetRingShine)
      product.removeEventListener('pointermove', handlePointerMove)
    }
  }, [canShowRingShine])

  return (
    <div
      className={clsx(styles.Product, className)}
      data-color-mode={theme}
      data-ring-shine={canShowRingShine ? true : undefined}
      data-rounded-corners={hasRoundedCorners ? true : undefined}
      ref={productRef}
      style={{
        '--product-border-color': 'var(--brand-color-border-subtle)',
        '--product-min-padding': minPadding,
        '--product-ring-color': ringColor,
      }}
    >
      <BackgroundGrid {...backgroundProps} className={styles.Background} theme={theme} />
      {hasFirstElement && <div className={clsx(styles.FirstElement, firstElementClass)}>{firstElement}</div>}
      <div className={styles.Content} data-media-position={mediaPosition}>
        {children}
      </div>
    </div>
  )
}
